import API_URL from '../config/apiConfig';
import { installBackendMapServices } from './backendMapServices';

let pending;
let callbackSequence = 0;
const hasLibraries = (maps) => Boolean(
  maps?.places && maps?.geometry && maps?.LatLng && maps?.LatLngBounds
);
const backendRequest = async (path, query = {}) => {
  const token = window.localStorage.getItem('token');
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 12000);
  try {
    const response = await fetch(`${API_URL}${path}?${new URLSearchParams(query)}`, {
      credentials: 'include', cache: 'no-store', signal: controller.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error_message || 'Maps service unavailable');
    return data;
  } finally {
    window.clearTimeout(timer);
  }
};

export function loadGoogleMaps() {
  if (pending) return pending;
  pending = (async () => {
    if (!hasLibraries(window.google?.maps) && !window.google?.maps?.importLibrary) {
      const config = await backendRequest('/maps/config', { app: 'dashboard', platform: 'web' });
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        // Keep the browser-side loader compliant even while a local backend
        // process has not yet been restarted after a Maps configuration edit.
        const scriptUrl = new URL(config.scriptUrl);
        const callbackName = `__instaportDashboardMapsReady${++callbackSequence}`;
        scriptUrl.searchParams.set('loading', 'async');
        scriptUrl.searchParams.set('callback', callbackName);
        script.src = scriptUrl.toString();
        script.async = true;
        const cleanup = () => {
          window.clearTimeout(timer);
          delete window[callbackName];
        };
        const timer = window.setTimeout(() => {
          cleanup();
          script.remove(); reject(new Error('Maps loading timed out'));
        }, 20000);
        // loading=async signals readiness through this callback, not the
        // script load event (which can precede places/geometry initialization).
        window[callbackName] = () => { cleanup(); resolve(); };
        script.onerror = () => { cleanup(); script.remove(); reject(new Error('Unable to load map display')); };
        document.head.appendChild(script);
      });
    }
    const maps = window.google?.maps;
    // Also handle an SDK already bootstrapped by another component while its
    // libraries are still loading. Do not inject a second Google script.
    if (!hasLibraries(maps) && maps?.importLibrary) {
      await Promise.all(['core', 'places', 'geometry'].map((name) => maps.importLibrary(name)));
    }
    if (!hasLibraries(maps)) throw new Error('Map display unavailable');
    installBackendMapServices(maps, backendRequest);
    window.dispatchEvent(new CustomEvent('google-maps-ready'));
    return maps;
  })().catch((error) => { pending = undefined; throw error; });
  return pending;
}
